import { Component, DestroyRef, OnInit } from '@angular/core';
import { ImageUploadModel } from '../../../../services/gui/image/model';
import { API, FixedAspectRatioImageDTO, PlayerHardwareDTO, SingleFileUpload } from '../../../../services/api/api';
import { ActivatedRoute } from '@angular/router';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { ImageUploadService } from '../../../../services/gui/image/image-upload.service';
import { ErrorService } from '../../../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '../../../../services/util.service';
import { NavigationService } from '@ngx-seatback/utils';
import { Clipping } from '../../../../components/image/model';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-player-hardware-details-overview',
  templateUrl: './player-hardware-details-overview.component.html',
  styleUrls: ['./player-hardware-details-overview.component.scss'],
  standalone: false,
})
export class PlayerHardwareDetailsOverviewComponent implements OnInit {
  logoImageUpload: ImageUploadModel;
  AmbianceImageUpload: ImageUploadModel;
  playerHardwareID: number;
  playerHardware: PlayerHardwareDTO;
  loading: boolean;
  logoUrl: string;
  depictionUrl: string;

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: API,
    private modal: ModalService,
    private imageUploadService: ImageUploadService,
    private errorHandler: ErrorService,
    public translate: TranslateService,
    public util: UtilService,
    private navService: NavigationService,
    private destroyRef: DestroyRef
  ) {
    this.logoImageUpload = new ImageUploadModel();
    this.AmbianceImageUpload = new ImageUploadModel();
    this.playerHardwareID = undefined;
    this.playerHardware = undefined;
    this.loading = true;
    this.logoUrl = './assets/img/channel-placeholder.png';
    this.depictionUrl = './assets/img/channel-placeholder.png';
  }

  ngOnInit(): void {
    this.activatedRoute.parent.params.subscribe(params => {
      this.playerHardwareID = +params.playerHardwareID;
      this.refreshFromServer();
    });
  }

  refreshFromServer() {
    this.loading = true;
    this.api
      .ca_md_PlayerHardware_retrieveById(this.playerHardwareID)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (p: PlayerHardwareDTO) => {
          this.playerHardware = p;
          this.logoUrl = this.playerHardware?.logo
            ? this.util.appendQueryParamToUrl(this.playerHardware.logo.url as unknown as string, '')
            : './assets/img/channel-placeholder.png';
          this.depictionUrl = this.playerHardware?.depiction
            ? this.util.appendQueryParamToUrl(this.playerHardware.depiction.url as unknown as string, '')
            : './assets/img/channel-placeholder.png';
        },
        error: error => {
          if (error.status && (+error.status === 403 || +error.status === 404)) {
            this.navService.navigateToPoint(this.util.RoutingID.products);
          } else {
            this.errorHandler.errorHandler(error);
          }
        },
      });
  }

  pickAmbiance(type: string) {
    this.AmbianceImageUpload?.destroySubscriptions();
    this.api
      .ca_md_PlayerHardware_retrieveById(this.playerHardwareID)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (p: PlayerHardwareDTO) => {
          this.AmbianceImageUpload.imageTransfer = this.imageUploadService.imageUpload(this.translate.instant('lang.globals.ambiance'));
          this.AmbianceImageUpload.imageTransfer.image = type === 'LOGO' ? p?.logo : p?.depiction;
          this.AmbianceImageUpload.subClippingChange = this.AmbianceImageUpload.imageTransfer.clippingChange.subscribe((res: Clipping) => {
            this.ambianceClippingChange(type, res);
          });
          this.AmbianceImageUpload.subFileChange = this.AmbianceImageUpload.imageTransfer.fileChange.subscribe((file: SingleFileUpload) => {
            this.ambianceFileChange(type, file);
          });
          this.AmbianceImageUpload.subDeletePress = this.AmbianceImageUpload.imageTransfer.deletePress.subscribe(() => {
            this.ambianceDeletePress(type);
          });
          this.AmbianceImageUpload.subModalClosed = this.AmbianceImageUpload.imageTransfer.modalClosed.subscribe(() => {
            this.ambianceUploadModalClosed(type);
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ambianceClippingChange(type: string, res: Clipping) {
    if (res) {
      this.applyClipping(this.AmbianceImageUpload, res);
      const transImage: FixedAspectRatioImageDTO = this.AmbianceImageUpload.imageTransfer.image;
      this.AmbianceImageUpload.imageTransfer.image = null;
      this.AmbianceImageUpload.imageTransfer.uploading = true;

      (type === 'LOGO'
        ? this.api.ca_md_PlayerHardware_clipLogo(this.playerHardwareID, transImage)
        : this.api.ca_md_PlayerHardware_clipDepiction(this.playerHardwareID, transImage)
      )
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (image: FixedAspectRatioImageDTO) => {
            this.AmbianceImageUpload.imageTransfer.image = image;
            this.AmbianceImageUpload.imageTransfer.uploading = false;
            this.refreshFromServer();
          },
          error: error => {
            this.modal.close('sbNavigationModal');
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  ambianceFileChange(type: string, file: SingleFileUpload) {
    this.AmbianceImageUpload.imageTransfer.image = null;
    this.AmbianceImageUpload.imageTransfer.uploading = true;

    (type === 'LOGO'
      ? this.api.ca_md_PlayerHardware_uploadLogo(this.playerHardwareID, file)
      : this.api.ca_md_PlayerHardware_uploadDepiction(this.playerHardwareID, file)
    )
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: FixedAspectRatioImageDTO) => {
          this.AmbianceImageUpload.imageTransfer.image = res;
          this.AmbianceImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ambianceDeletePress(type: string) {
    this.AmbianceImageUpload.imageTransfer.image = null;
    this.AmbianceImageUpload.imageTransfer.uploading = true;

    (type === 'LOGO'
      ? this.api.ca_md_PlayerHardware_deleteLogo(this.playerHardwareID)
      : this.api.ca_md_PlayerHardware_deleteDepiction(this.playerHardwareID)
    )
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: boolean) => {
          if (res) {
            this.AmbianceImageUpload.imageTransfer.image = null;
          }
          this.AmbianceImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ambianceUploadModalClosed(type: string) {
    this.AmbianceImageUpload.destroySubscriptions();
    this.refreshFromServer();
  }

  applyClipping(imageUpload: ImageUploadModel, res: Clipping) {
    imageUpload.imageTransfer.image.clippingTLX = res.clippingTLX;
    imageUpload.imageTransfer.image.clippingTLY = res.clippingTLY;
    imageUpload.imageTransfer.image.clippingBRX = res.clippingBRX;
    imageUpload.imageTransfer.image.clippingBRY = res.clippingBRY;
  }
}
